/**
 * Действия режимов «Скорость» и «Точность».
 *
 * Занятие состоит из нескольких коротких прогонов: каждый прогон двигает
 * лестницу темпа или счётчик попыток, и только последний закрывает занятие.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { SessionPayload } from '@type2x/feature-sync';
import type { TempoMode } from '@type2x/feature-tempo';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { advanceRun, finishTempo, tempoScreen } from '../state/tempo.js';

/** Действия режимов, которые приложение передаёт экранам. */
export interface TempoActions {
  readonly onStartTempo: (mode: TempoMode) => void;
  readonly onTempoRunDone: (run: TypingSession) => void;
}

/** Что нужно режимам от приложения. */
export interface TempoDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useTempo(runtime: Runtime, deps: TempoDeps): TempoActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartTempo = useCallback(
    (mode: TempoMode) => {
      if (workspace === null) return;
      setScreen(tempoScreen(runtime, workspace, mode));
    },
    [runtime, workspace, setScreen],
  );

  const onTempoRunDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'tempo') return;

      const advanced = advanceRun(screen.session, run);
      if (!advanced.finished) {
        setScreen({ ...screen, session: advanced, sessionId: screen.sessionId });
        return;
      }

      const transition = finishTempo(runtime, workspace, advanced, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartTempo, onTempoRunDone };
}
